import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationListQuery,
  UpdateNotificationPreferencesInput,
} from '@teentack/shared';
import {
  fetchNotificationPreferences,
  fetchNotifications,
  fetchUnreadCount,
  markNotificationsRead,
  saveNotificationPreferences,
} from './notifications.api';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (query: Partial<NotificationListQuery>) => ['notifications', 'list', query] as const,
  unread: ['notifications', 'unread'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

export function useNotifications(query: Partial<NotificationListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => fetchNotifications(query),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * The bell badge.
 *
 * Polled rather than pushed: the stack has no realtime transport, and a count
 * query is one indexed `COUNT(*)`. A minute is slow enough to be free and fast
 * enough that nobody notices the delay.
 */
export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: fetchUnreadCount,
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationIds?: string[]) => markNotificationsRead(notificationIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: fetchNotificationPreferences,
    enabled,
  });
}

export function useSaveNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateNotificationPreferencesInput) => saveNotificationPreferences(input),
    onSuccess: (saved) => {
      // The server returns the merged result, so seed it rather than refetch.
      queryClient.setQueryData(notificationKeys.preferences, saved);
    },
  });
}
